import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Blocker } from 'react-router-dom';
import { useBlocker } from 'react-router-dom';

import type { NavigationAttempt, NavigationGuard, NavigationTransition } from 'types/navigation';
import { idleBlocker, NavigationBlockerContext } from './NavigationBlockerContext';

export const NavigationBlockerProvider = ({ children }: PropsWithChildren) => {
  const guards = useRef(new Map<string, NavigationGuard>());
  const attempt = useRef<NavigationAttempt | null>(null);
  const approvedActions = useRef(0);
  const [transition, setTransition] = useState<NavigationTransition | null>(null);
  const transitionRef = useRef(transition);
  const [registryVersion, setRegistryVersion] = useState(0);
  transitionRef.current = transition;

  const blocker = useBlocker(
    useCallback((args) => {
      if (approvedActions.current > 0 && args.historyAction !== 'POP') return false;
      const matching = Array.from(guards.current.values()).filter((guard) => guard.shouldBlock(args));
      const selectGuard = (group: NavigationGuard['group']) =>
        matching
          .filter((guard) => guard.group === group)
          .reverse()
          .sort((first, second) => second.priority - first.priority)
          .at(0);
      const selected = [selectGuard('document'), selectGuard('page')].filter((guard): guard is NavigationGuard => Boolean(guard));
      if (!selected.length) return false;
      attempt.current = { args, guardIds: selected.map((guard) => guard.id) };
      return true;
    }, [])
  );
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  const updateTransition = useCallback((value: NavigationTransition | null) => {
    transitionRef.current = value;
    setTransition(value);
  }, []);

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      if (transitionRef.current) updateTransition(null);
      return;
    }
    if (transitionRef.current?.args.nextLocation.key === blocker.location.key) return;
    if (attempt.current) updateTransition({ ...attempt.current, index: 0 });
  }, [blocker, updateTransition]);

  const proceed = useCallback(
    (expectedId?: string, expectedKey?: string) => {
      const current = transitionRef.current;
      const currentBlocker = blockerRef.current;
      if (!current || currentBlocker.state !== 'blocked' || current.args.nextLocation.key !== currentBlocker.location.key) return;
      if (expectedId && current.guardIds[current.index] !== expectedId) return;
      if (expectedKey && current.args.nextLocation.key !== expectedKey) return;
      const nextIndex = current.guardIds.findIndex((id, index) => index > current.index && guards.current.get(id)?.shouldBlock(current.args));
      if (nextIndex >= 0) updateTransition({ ...current, index: nextIndex });
      else {
        updateTransition(null);
        currentBlocker.proceed();
      }
    },
    [updateTransition]
  );

  const reset = useCallback(
    (expectedId: string, expectedKey: string) => {
      const current = transitionRef.current;
      if (!current || current.guardIds[current.index] !== expectedId || current.args.nextLocation.key !== expectedKey) return;
      updateTransition(null);
      if (blockerRef.current.state === 'blocked') blockerRef.current.reset();
    },
    [updateTransition]
  );

  const register = useCallback((guard: NavigationGuard) => {
    guards.current.set(guard.id, guard);
  }, []);

  const unregister = useCallback((id: string) => {
    guards.current.delete(id);
    setRegistryVersion((value) => value + 1);
  }, []);

  useEffect(() => {
    const current = transitionRef.current;
    if (current && !guards.current.has(current.guardIds[current.index])) proceed();
  }, [registryVersion, transition, proceed]);

  const activeId = transition?.guardIds[transition.index];
  const activeBlocker = useMemo<Blocker>(
    () =>
      blocker.state === 'blocked' && activeId
        ? {
            state: 'blocked',
            location: blocker.location,
            proceed: () => proceed(activeId, blocker.location.key),
            reset: () => reset(activeId, blocker.location.key),
          }
        : idleBlocker,
    [activeId, blocker, proceed, reset]
  );
  const getBlocker = useCallback((id: string) => (id === activeId ? activeBlocker : idleBlocker), [activeId, activeBlocker]);
  const runApprovedAction = useCallback(async <T,>(action: () => Promise<T>): Promise<T> => {
    const hasTransition = Boolean(transitionRef.current);
    if (hasTransition) approvedActions.current += 1;
    try {
      return await action();
    } finally {
      if (hasTransition) approvedActions.current -= 1;
    }
  }, []);
  const api = useMemo(
    () => ({ register, unregister, getBlocker, navigationPending: blocker.state === 'blocked', runApprovedAction }),
    [register, unregister, getBlocker, blocker.state, runApprovedAction]
  );

  return <NavigationBlockerContext.Provider value={api}>{children}</NavigationBlockerContext.Provider>;
};
